import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ROUTES, postLoginRedirectPath } from '../../routes';

const AuthBootSpinner = () => (
  <div className="flex min-h-screen items-center justify-center bg-neutral-50">
    <span className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" aria-label="Loading session" />
  </div>
);

export const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAuthReady } = useAuth();
  const location = useLocation();
  if (!isAuthReady) return <AuthBootSpinner />;
  if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  return <>{children}</>;
};

export const RoleGuard: React.FC<{ permission: string; children: React.ReactNode }> = ({ permission, children }) => {
  const { permissions, user } = useAuth();
  const allowed = permissions.includes('*') || permissions.includes(permission);
  if (!allowed) {
    // Prefer dashboard over a dead-end `/403` (e.g. Editor hitting Team, or stale returnTo).
    const home = user ? postLoginRedirectPath(user.role, null) : ROUTES.EDITORIAL;
    return <Navigate to={home} replace />;
  }
  return <>{children}</>;
};

/**
 * Workspace Owner / Admin only — platform SuperAdmins and Editors are redirected silently
 * (Template Studio spec §2.1: no error message). Matches the API's owner/admin check.
 */
export const WorkspaceManagerGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== 'Owner' && user?.role !== 'Admin') {
    const home = user ? postLoginRedirectPath(user.role, null) : ROUTES.EDITORIAL;
    return <Navigate to={home} replace />;
  }
  return <>{children}</>;
};
